/**
 * The PIN lock screen (FM Technician rebuild B17, D14: "4-digit PIN for quick
 * unlock").
 *
 * After LOCK_AFTER_MS without a touch (or the app coming back after that long
 * in the background) a technician who has set a PIN sees this over the whole
 * app. The PIN goes to POST /auth/pin-unlock WITH this device's refresh token
 * — the server re-issues tokens only for a live sign-in on this device, and
 * only for its own person. Five wrong PINs clear the PIN and end the sign-in:
 * the screen then sends them to the password.
 *
 * A raw fetch, not the api client: a 401 here is "wrong PIN", and the client's
 * 401 handling would refresh and replay it.
 *
 * The locked state survives a reload (components/Tech/lockState, per person),
 * so reloading the page is not a way past it.
 *
 * Review R2-2: the page stays MOUNTED under the lock (unsaved work kept), so
 * the lock is a real dialog and the TOP one: a sheet the page had open is a
 * dialog too, and the newest dialog is the one that makes everything else
 * inert and hidden from a screen reader. A plain overlay would itself have
 * been made inert by that open sheet — a keypad nobody could press. It never
 * closes on Escape or a click outside; only an unlock or a sign-out ends it.
 */
import { useState } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import Keypad from '@/components/Tech/Keypad';
import { apiUrl } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';

export { LOCK_AFTER_MS, isLocked, setLocked } from '@/components/Tech/lockState';

/**
 * While a technician with a PIN is signed in, the PIN lock TAKES OVER from the
 * 30-minute idle sign-out (hooks/useIdleLogout): signing a technician out
 * mid-shift would throw away the point of a quick unlock (D14). The lock is
 * no weaker — unlocking needs the server to check the PIN against this
 * device's live sign-in, and five wrong tries end it.
 */
let idleLockOwner: string | null = null;
export function setIdleLockOwner(uid: string | null): void { idleLockOwner = uid; }
export function idleLockTakesOver(uid: string | null | undefined): boolean {
  return !!uid && idleLockOwner === uid;
}

export default function PinLock({ onUnlocked, onUsePassword }: {
  onUnlocked: () => void;
  /** Sign out and go to the password sign-in. */
  onUsePassword: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const setTokens = useAuthStore((s) => s.setTokens);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mustSignIn, setMustSignIn] = useState(false);

  const submit = async (value: string) => {
    const refresh = useAuthStore.getState().refreshToken;
    if (!user || !refresh) { setMustSignIn(true); setError('This sign-in has ended. Sign in with your password.'); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(apiUrl('/api/auth/pin-unlock'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: value, refresh_token: refresh, user_id: user.id }),
      });
      const body = await res.json().catch(() => ({})) as {
        data?: { access_token?: string; refresh_token?: string }; detail?: unknown;
      };
      if (res.ok && body.data?.access_token && body.data.refresh_token) {
        setTokens(body.data.access_token, body.data.refresh_token);
        setPin('');
        onUnlocked();
        return;
      }
      const detail = typeof body.detail === 'string' ? body.detail
        : res.status === 429 ? 'Too many tries. Wait a minute and try again.' : 'That did not work. Try again.';
      setError(detail);
      setPin('');
      // Anything but a plain wrong PIN means the password is the only way on.
      if (res.status === 401 && !detail.startsWith('Wrong PIN.')) setMustSignIn(true);
    } catch (e) {
      console.warn('PIN unlock did not reach the server', e);
      setError('No connection. Your PIN is checked by the server — try again when you have a signal.');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => undefined} className="relative z-[80]">
    <DialogPanel
         className="tech fixed inset-0 z-[80] flex flex-col items-center justify-center overflow-y-auto bg-surface p-4"
         data-testid="pin-lock">
      <div className="w-full max-w-sm space-y-6 text-center">
        <LockClosedIcon className="mx-auto h-10 w-10 text-brand-700" aria-hidden="true" />
        <div>
          <DialogTitle as="h1" id="pin-lock-title" className="text-xl font-semibold text-content">Locked</DialogTitle>
          <p className="mt-1 text-base text-content-muted">
            {user ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() : ''} — enter your PIN to carry on.
          </p>
        </div>
        {!mustSignIn && (
          <Keypad value={pin} onChange={setPin} onComplete={(v) => { void submit(v); }}
                  label="Enter your PIN" disabled={busy} error={error} testid="pin-lock-keypad" />
        )}
        {mustSignIn && error && (
          <p role="alert" className="text-base text-danger-700" data-testid="pin-lock-error">{error}</p>
        )}
        <button type="button" className={mustSignIn ? 'btn-primary w-full' : 'btn-ghost w-full'}
                onClick={onUsePassword} data-testid="pin-lock-password">
          Sign in with my password instead
        </button>
        <p className="text-sm text-content-muted">Work you saved on this phone stays here for you.</p>
      </div>
    </DialogPanel>
    </Dialog>
  );
}
